import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

export enum PermissionAction {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export enum CrmPermissionCode {
  CrmMemberCheckIn = 'crm-member-check-in',
  CrmClassSummary = 'crm-class-summary',
  CrmTrainerSummary = 'crm-trainer-summary',
  CrmSwimmingSummary = 'crm-swimming-summary',
  CrmMemberSummary = 'crm-member-summary',
  CrmCheckInSummary = 'crm-check-in-summary',
  CrmOpportunityAnalysis = 'crm-opportunity-analysis',
  CrmSalesSummary = 'crm-sales-summary',
  CrmIndividualSaleSummary = 'crm-individual-sale-summary',
  CrmCreditSummary = 'crm-credit-summary',
  CrmRevenueBreakdown = 'crm-revenue-breakdown',
  CrmTodoNewLead = 'crm-todo-new-lead',
  CrmTodoUpSell = 'crm-todo-up-sell',
  CrmLeadNewLead = 'crm-lead-new-lead',
  CrmLeadUpSell = 'crm-lead-up-sell',
  CrmSalesTodoList = 'crm-sales-todo-list',
  CrmSalesList = 'crm-sales-list',
  CrmLeadAssignment = 'crm-lead-assignment',
  CrmClassSchedule = 'crm-class-schedule',
  CrmClassCategories = 'crm-class-categories',
  CrmMemberList = 'crm-member-list',
  CrmMemberFeedback = 'crm-member-feedback',
  CrmMemberPackages = 'crm-member-packages',
  CrmArchivedPackages = 'crm-archived-packages',
  CrmPtSchedule = 'crm-pt-schedule',
  CrmPtList = 'crm-pt-list',
  CrmTrainingProgram = 'crm-training-program',
  CrmPtGoalSettings = 'crm-pt-goal-settings',
  CrmSwimmingSchedule = 'crm-swimming-schedule',
  CrmSwimmingCoachList = 'crm-swimming-coach-list',
  CrmSwimmingGoalSettings = 'crm-swimming-goal-settings',
  CrmSalesCommission = 'crm-sales-commission',
  CrmTrainerConduct = 'crm-trainer-conduct',
  CrmRoomList = 'crm-room-list',
  CrmRoomAvailability = 'crm-room-availability',
  CrmFacilityList = 'crm-facility-list',
  CrmFacilityAvailability = 'crm-facility-availability',
  CrmProducts = 'crm-products',
  CrmStockReport = 'crm-stock-report',
  CrmReceipts = 'crm-receipts',
  CrmCreditTransfer = 'crm-credit-transfer',
  CrmSaleReport = 'crm-sale-report',
  CrmTaxReport = 'crm-tax-report',
  CrmCreditReport = 'crm-credit-report',
  CrmQuotaReport = 'crm-quota-report',
  CrmStaffList = 'crm-staff-list',
  CrmClubSettings = 'crm-club-settings',
  CrmIntegrations = 'crm-integrations',
  CrmWorkplaceIntegration = 'crm-workplace-integration',
  CrmLactateTest = 'crm-lactate-test',
  CrmHumanArchitect = 'crm-human-architect',
  // Loyalty (QTY-1707): points/tiers/redemption. Independent of credit (wallet).
  CrmLoyaltySummary = 'crm-loyalty-summary',
  CrmLoyaltyConfig = 'crm-loyalty-config',
  CrmLoyaltyRedeem = 'crm-loyalty-redeem',
  CrmLoyaltyReport = 'crm-loyalty-report',
  CrmEquipmentList = 'crm-equipment-list',
  CrmWorkOrders = 'crm-work-orders',
  CrmDailyInspection = 'crm-daily-inspection',
  CrmPMSchedule = 'crm-pm-schedule',
  CrmVendorList = 'crm-vendor-list',
  CrmEquipmentReport = 'crm-equipment-report',
  CrmCampaignList = 'crm-campaign-list',
  CrmCampaignReport = 'crm-campaign-report',
  // Manager-facing audit trail (value must match backend FEATURE_CODE.CRM_AUDIT_LOGS).
  CrmAuditLog = 'crm-audit-logs',
  // Wellness & Spa module — board + settings share one code (value must match backend
  // FEATURE_CODE.CRM_WELLNESS_SPA). Until `pnpm sync:role-permissions` runs per env the menu
  // entry stays hidden, which is the intended fail-closed behaviour.
  CrmWellnessSpa = 'crm-wellness-spa',
}

export enum CrmRoleIdCode {
  ClubDirector = 1,
  ClubManager = 2,
  MarketingManager = 3,
  SaleManager = 4,
  SaleStaff = 5,
  FitnessManager = 6,
  FitnessStaffTrainer = 7,
  CustomerServiceManager = 8,
  CustomerServiceStaffAdmin = 9,
  AquaticManager = 10,
  AquaticStaff = 11,
  MobilityMindfullness = 12,
  RecoveryManager = 13,
  RecoveryStaff = 14,
}

@Serializable()
export class Profile extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('roleId')
  roleId!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('email')
  email!: string

  @JsonProperty('position')
  position!: string

  @JsonProperty('presetCodes')
  presetCodes?: string[]

  /**
   * QTY-2042: staff soft-delete status. Absent on backends predating the
   * feature, which is treated as ACTIVE — only an explicit INACTIVE blocks the
   * session.
   */
  @JsonProperty('accountStatus')
  accountStatus?: 'ACTIVE' | 'INACTIVE'
}

@Serializable()
export class Permission extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('action')
  action!: PermissionAction

  @JsonProperty({
    path: 'feature.displayName',
  })
  name!: string

  @JsonProperty({
    path: 'feature.code',
  })
  code!: CrmPermissionCode
}

@Serializable()
export class UserRole extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code?: string

  @JsonProperty('name')
  name!: string

  @JsonProperty({
    path: 'roleFeatureAction',
    elementType: Permission,
  })
  permissions!: Permission[]

  @JsonProperty('position')
  position?: string

  @JsonProperty('memberType')
  memberType?: string
}
